import type { ReactNode } from 'react';
import { Icon, type IconName } from './Icon';

/**
 * Cartoes de secao (RNF15).
 *
 * `SectionCard` e o bloco padrao das paginas: titulo, uma linha de contexto,
 * acoes a direita e o conteudo. `Panel` e a mesma superficie sem cabecalho,
 * para compor grades. `HeroBanner` e a faixa de identidade de um funil ou
 * pagina (icone grande, titulo, selos, descricao), com o UNICO gradiente do
 * painel discretamente ao fundo.
 */

export function Panel({ children, className = '', tone = 'surface' }: { readonly children: ReactNode; readonly className?: string; readonly tone?: 'surface' | 'elevated' | 'brand' | 'good' | 'warning' | 'critical' | 'accent' }): JSX.Element {
  const base: Readonly<Record<typeof tone, string>> = {
    surface: 'border-line bg-surface',
    elevated: 'border-line bg-elevated',
    brand: 'border-brand/40 bg-brand/5',
    accent: 'border-accent/40 bg-accent/5',
    good: 'border-status-good/40 bg-status-good/5',
    warning: 'border-status-warning/50 bg-status-warning/5',
    critical: 'border-status-critical/40 bg-status-critical/5',
  };
  return <div className={`rounded-2xl border shadow-soft ${base[tone]} ${className}`}>{children}</div>;
}

export function SectionCard({
  title,
  description,
  icon,
  aside,
  children,
  id,
  className = '',
  padded = true,
}: {
  readonly title: string;
  readonly description?: ReactNode;
  readonly icon?: IconName;
  readonly aside?: ReactNode;
  readonly children: ReactNode;
  readonly id?: string;
  readonly className?: string;
  /** `false` quando o conteudo e uma tabela que precisa encostar nas bordas. */
  readonly padded?: boolean;
}): JSX.Element {
  const headingId = id === undefined ? undefined : `${id}-titulo`;
  return (
    <section aria-labelledby={headingId} id={id} className={`flex flex-col rounded-2xl border border-line bg-surface shadow-soft ${className}`}>
      <div className="flex flex-wrap items-start justify-between gap-3 px-5 pt-5">
        <div className="min-w-0">
          <h2 id={headingId} className="flex items-center gap-2 text-base font-semibold text-ink">
            {icon === undefined ? null : (
              <span aria-hidden="true" className="grid h-7 w-7 place-items-center rounded-lg bg-brand/10 text-brand">
                <Icon name={icon} size="sm" />
              </span>
            )}
            {title}
          </h2>
          {description === undefined ? null : <p className="mt-0.5 text-sm text-ink-muted">{description}</p>}
        </div>
        {aside === undefined ? null : <div className="flex flex-wrap items-center gap-2">{aside}</div>}
      </div>
      <div className={padded ? 'px-5 pb-5 pt-4' : 'pt-4'}>{children}</div>
    </section>
  );
}

export function HeroBanner({
  icon,
  title,
  badges,
  description,
  aside,
  backTo,
  tone = 'brand',
}: {
  readonly icon: IconName;
  readonly title: string;
  readonly badges?: ReactNode;
  readonly description: ReactNode;
  readonly aside?: ReactNode;
  /** Botao "voltar" a esquerda do icone. */
  readonly backTo?: ReactNode;
  readonly tone?: 'brand' | 'accent' | 'info' | 'good';
}): JSX.Element {
  const cor: Readonly<Record<typeof tone, string>> = {
    brand: 'bg-brand text-white',
    accent: 'bg-accent text-white',
    info: 'bg-info text-white',
    good: 'bg-status-good text-white',
  };
  return (
    <header className="relative overflow-hidden rounded-2xl border border-line bg-surface px-5 py-5 shadow-soft">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-hero opacity-[0.07]" />
      <div className="relative flex flex-wrap items-center justify-between gap-4">
        <div className="flex min-w-0 items-center gap-4">
          {backTo}
          <span aria-hidden="true" className={`grid h-12 w-12 shrink-0 place-items-center rounded-xl shadow-soft ${cor[tone]}`}>
            <Icon name={icon} size="lg" />
          </span>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 id="titulo-da-pagina" className="text-xl font-semibold tracking-tight text-ink sm:text-2xl">
                {title}
              </h1>
              {badges}
            </div>
            <p className="mt-0.5 max-w-2xl text-sm text-ink-muted">{description}</p>
          </div>
        </div>
        {aside === undefined ? null : <div className="flex flex-wrap items-center gap-2">{aside}</div>}
      </div>
    </header>
  );
}

/** Linha de rodape explicativa, com o ponto de informacao. Uma frase, no maximo duas. */
export function Footnote({ children }: { readonly children: ReactNode }): JSX.Element {
  return (
    <p className="flex items-start gap-2 text-xs text-ink-subtle">
      <Icon name="info" size="xs" className="mt-0.5" />
      <span>{children}</span>
    </p>
  );
}
